import { CandleDataPoint, CandleFrequencyOption } from './types';

// Frequency options with millisecond values
export const CANDLE_FREQUENCIES: CandleFrequencyOption[] = [
    { value: '1s', label: '1s', ms: 1_000 },
    { value: '15s', label: '15s', ms: 15_000 },
    { value: '1m', label: '1m', ms: 60_000 },
    { value: '15m', label: '15m', ms: 900_000 },
    { value: '1h', label: '1h', ms: 3_600_000 },
    { value: '4h', label: '4h', ms: 14_400_000 },
    { value: '1d', label: '1d', ms: 86_400_000 },
    { value: '1w', label: '1w', ms: 604_800_000 },
];

export interface FrequencyResult {
    frequencyMs: number;
    confidence: number;
}

/**
 * Detect the frequency of candle data by analyzing timestamp differences.
 * Uses mode (most common value) to determine the dominant frequency.
 */
export function detectFrequency(candles: CandleDataPoint[]): FrequencyResult {
    if (candles.length < 2) {
        return { frequencyMs: 60000, confidence: 0 }; // Default to 1 minute
    }

    // Sort by timestamp
    const sorted = [...candles].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    // Calculate all consecutive time differences
    const diffs: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
        const diff = sorted[i].timestamp.getTime() - sorted[i - 1].timestamp.getTime();
        diffs.push(diff);
    }

    // Count occurrences of each difference (mode calculation)
    const counts = new Map<number, number>();
    for (const diff of diffs) {
        counts.set(diff, (counts.get(diff) || 0) + 1);
    }

    // Find the mode (most common difference)
    let mode = 60000; // Default to 1 minute
    let maxCount = 0;
    for (const [diff, count] of counts) {
        if (count > maxCount) {
            maxCount = count;
            mode = diff;
        }
    }

    // Calculate confidence as the proportion of gaps matching the mode
    const confidence = diffs.length > 0 ? maxCount / diffs.length : 0;

    return {
        frequencyMs: mode,
        confidence,
    };
}

/**
 * Options for controlling memory usage during candle padding
 */
export interface PadCandlesOptions {
    /** Maximum total gaps to fill (default: 100000) */
    maxGapsToFill?: number;
    /** Maximum consecutive gaps before skipping to next real candle (default: 100000) */
    maxConsecutiveGaps?: number;
    /** If true, skip padding for sparse data (default: false) */
    skipPaddingIfSparse?: boolean;
    /** Gap ratio above which data is considered sparse (default: 0.5 = 50%) */
    sparseThreshold?: number;
}

const DEFAULT_PAD_OPTIONS: Required<PadCandlesOptions> = {
    maxGapsToFill: 10000,
    maxConsecutiveGaps: 1000,
    skipPaddingIfSparse: true,
    sparseThreshold: 0.5,
};

/**
 * Pad candles to create a continuous time series with memory safety limits.
 * Missing candles are filled with the previous candle's close price.
 *
 * Memory safety features:
 * - Limits total gaps filled to prevent memory explosion
 * - Limits consecutive gaps to avoid filling huge data holes
 * - Detects sparse data and optionally skips padding
 */
export function padCandles(
    candles: CandleDataPoint[],
    frequencyMs?: number,
    options: PadCandlesOptions = {}
): CandleDataPoint[] {
    if (candles.length === 0) {
        return [];
    }

    const opts = { ...DEFAULT_PAD_OPTIONS, ...options };

    // Sort by timestamp
    const sorted = [...candles].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    // Detect frequency if not provided
    const freq = frequencyMs ?? detectFrequency(sorted).frequencyMs;

    // Calculate sparseness
    const firstTs = sorted[0].timestamp.getTime();
    const lastTs = sorted[sorted.length - 1].timestamp.getTime();
    const expectedCandles = Math.ceil((lastTs - firstTs) / freq) + 1;
    const gapRatio = (expectedCandles - sorted.length) / expectedCandles;

    // Skip padding for sparse data if configured
    if (opts.skipPaddingIfSparse && gapRatio > opts.sparseThreshold) {
        return sorted.map(c => ({ ...c, isFilled: false }));
    }

    // Create a map of existing candles by timestamp
    const candleMap = new Map<number, CandleDataPoint>();
    for (const candle of sorted) {
        candleMap.set(candle.timestamp.getTime(), candle);
    }

    const result: CandleDataPoint[] = [];
    let previousCandle: CandleDataPoint | null = null;
    let totalGapsFilled = 0;
    let consecutiveGaps = 0;

    for (let ts = firstTs; ts <= lastTs; ts += freq) {
        const existing = candleMap.get(ts);

        if (existing) {
            consecutiveGaps = 0;
            result.push({
                ...existing,
                isFilled: false,
            });
            previousCandle = existing;
        } else if (previousCandle) {
            // Check memory limits
            if (totalGapsFilled >= opts.maxGapsToFill) {
                break;
            }

            consecutiveGaps++;
            if (consecutiveGaps > opts.maxConsecutiveGaps) {
                // Skip to next real candle
                const nextReal = findNextRealCandle(ts, lastTs, freq, candleMap);
                if (nextReal !== null) {
                    ts = nextReal - freq;
                    consecutiveGaps = 0;
                }
                continue;
            }

            // Create padded candle
            const paddedCandle: CandleDataPoint = {
                timestamp: new Date(ts),
                open: previousCandle.close,
                high: previousCandle.close,
                low: previousCandle.close,
                close: previousCandle.close,
                baseVolume: 0,
                quoteVolume: 0,
                tradeCount: 0,
                vwap: previousCandle.close,
                openTimestamp: new Date(ts),
                closeTimestamp: new Date(ts + freq),
                marketInfo: previousCandle.marketInfo,
                isFilled: true,
            };
            result.push(paddedCandle);
            previousCandle = paddedCandle;
            totalGapsFilled++;
        }
    }

    return result;
}

/**
 * Helper to find next real candle timestamp
 */
function findNextRealCandle(
    currentTs: number,
    lastTs: number,
    freq: number,
    candleMap: Map<number, CandleDataPoint>
): number | null {
    for (let ts = currentTs + freq; ts <= lastTs; ts += freq) {
        if (candleMap.has(ts)) {
            return ts;
        }
    }
    return null;
}

/**
 * Get available frequencies based on base frequency.
 * Only returns frequencies >= baseFrequencyMs.
 */
export function getAvailableFrequencies(baseFrequencyMs: number): CandleFrequencyOption[] {
    return CANDLE_FREQUENCIES.filter(freq => freq.ms >= baseFrequencyMs);
}

/**
 * Get the closest standard frequency for a detected frequency.
 * Snaps to the nearest standard frequency that is <= detected.
 */
export function snapToStandardFrequency(detectedMs: number): CandleFrequencyOption {
    // Find the largest standard frequency that is <= detectedMs
    let closest = CANDLE_FREQUENCIES[0];

    for (const freq of CANDLE_FREQUENCIES) {
        if (freq.ms <= detectedMs) {
            closest = freq;
        }
    }

    return closest;
}

/**
 * Aggregate candles from base frequency to target frequency.
 * Groups candles by target period and computes OHLCV values.
 */
export function aggregateCandles(
    candles: CandleDataPoint[],
    targetFrequencyMs: number
): CandleDataPoint[] {
    if (candles.length === 0) return [];

    // Sort candles by timestamp
    const sorted = [...candles].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    // Group candles by target period
    const groups = new Map<number, CandleDataPoint[]>();

    for (const candle of sorted) {
        // Calculate period start (floor to target frequency)
        const periodStart = Math.floor(candle.timestamp.getTime() / targetFrequencyMs) * targetFrequencyMs;

        if (!groups.has(periodStart)) {
            groups.set(periodStart, []);
        }
        groups.get(periodStart)!.push(candle);
    }

    // Aggregate each group
    const aggregated: CandleDataPoint[] = [];

    for (const [periodStart, groupCandles] of groups) {
        if (groupCandles.length === 0) continue;

        // Sort group by timestamp
        groupCandles.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        const first = groupCandles[0];
        const last = groupCandles[groupCandles.length - 1];

        // Compute aggregated values
        const open = first.open;
        const close = last.close;
        const high = Math.max(...groupCandles.map(c => c.high));
        const low = Math.min(...groupCandles.map(c => c.low));
        const baseVolume = groupCandles.reduce((sum, c) => sum + c.baseVolume, 0);
        const quoteVolume = groupCandles.reduce((sum, c) => sum + c.quoteVolume, 0);
        const tradeCount = groupCandles.reduce((sum, c) => sum + c.tradeCount, 0);

        // Compute volume-weighted average price (VWAP)
        const totalWeightedPrice = groupCandles.reduce(
            (sum, c) => sum + c.vwap * c.baseVolume, 0
        );
        const vwap = baseVolume > 0 ? totalWeightedPrice / baseVolume : close;

        aggregated.push({
            timestamp: new Date(periodStart),
            open,
            high,
            low,
            close,
            baseVolume,
            quoteVolume,
            tradeCount,
            vwap,
            openTimestamp: first.openTimestamp,
            closeTimestamp: new Date(periodStart + targetFrequencyMs),
            marketInfo: first.marketInfo,
            isFilled: false, // Aggregated candles are real data
        });
    }

    // Sort by timestamp
    return aggregated.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}
